import { createGLTransaction, GLTransaction, GLTransactionEntry, deleteGLTransaction } from '../firebase/glTransactionServices';
import { getGLAccountByNumber } from '../firebase/glAccountServices';
import { PurchaseReceipt, getPurchaseReceiptById } from '../firebase/inventoryCostServices';
import { Timestamp } from 'firebase/firestore';
import { updatePurchaseReceiptWithGLTransaction, removePurchaseReceiptGLTransaction } from '../firebase/inventoryCostServices';

// Interface for GL entry preview
export interface GLEntryPreview {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  accountId?: string; // Will be populated when creating actual GL entries
}

// GL Account numbers for purchase receipts
const INVENTORY_GL_ACCOUNT = { accountNumber: '1300002', name: 'Inventory Balances' };
const ACCOUNTS_PAYABLE_GL_ACCOUNT = { accountNumber: '3200001', name: 'Account Payable - General' };

/**
 * Generate GL entry previews for purchase receipt
 * 
 * @param purchaseReceipt Purchase receipt data
 * @returns Array of GL entry previews
 */
export const generatePurchaseReceiptGLEntryPreviews = (purchaseReceipt: PurchaseReceipt): GLEntryPreview[] => {
  const entries: GLEntryPreview[] = [];
  
  // Add debit entry for inventory
  entries.push({
    accountNumber: INVENTORY_GL_ACCOUNT.accountNumber,
    accountName: INVENTORY_GL_ACCOUNT.name,
    debit: purchaseReceipt.totalAmount,
    credit: 0
  });
  
  // Add credit entry for accounts payable
  entries.push({
    accountNumber: ACCOUNTS_PAYABLE_GL_ACCOUNT.accountNumber,
    accountName: ACCOUNTS_PAYABLE_GL_ACCOUNT.name,
    debit: 0,
    credit: purchaseReceipt.totalAmount
  });
  
  return entries;
};

/**
 * Create GL transaction from purchase receipt
 * 
 * @param purchaseReceipt Purchase receipt data
 * @param glEntryPreviews GL entry previews (optional - if not provided, will generate from purchaseReceipt)
 * @returns Promise with the created GL transaction ID
 */
/**
 * Reverse a purchase receipt's GL transaction
 * 
 * @param receiptId The ID of the purchase receipt
 * @returns Promise that resolves when the reversal is complete
 */
export const reversePurchaseReceiptGLTransaction = async (receiptId: string): Promise<void> => {
  try {
    // Get the purchase receipt
    const receipt = await getPurchaseReceiptById(receiptId);
    
    if (!receipt) {
      throw new Error(`Purchase receipt with ID ${receiptId} not found`);
    }
    
    if (!receipt.glTransactionId) {
      throw new Error(`Purchase receipt with ID ${receiptId} does not have a GL transaction`);
    }
    
    // Delete the GL transaction
    await deleteGLTransaction(receipt.glTransactionId);
    
    // Remove the GL transaction ID from the purchase receipt
    await removePurchaseReceiptGLTransaction(receiptId);
  } catch (error) {
    console.error('Error reversing purchase receipt GL transaction:', error);
    throw error;
  }
};

/**
 * Create GL transaction from purchase receipt
 * 
 * @param purchaseReceipt Purchase receipt data
 * @param glEntryPreviews GL entry previews (optional - if not provided, will generate from purchaseReceipt)
 * @returns Promise with the created GL transaction ID
 */
export const createPurchaseReceiptGLTransaction = async (
  purchaseReceipt: PurchaseReceipt,
  glEntryPreviews?: GLEntryPreview[]
): Promise<string> => {
  try {
    // If no previews provided, generate them
    const entries = glEntryPreviews || generatePurchaseReceiptGLEntryPreviews(purchaseReceipt);
    
    // Validate that debits = credits
    const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      throw new Error('GL entries are not balanced. Total debits must equal total credits.');
    }
    
    // Convert entry previews to GL transaction entries
    const glEntries: GLTransactionEntry[] = [];
    
    for (const entry of entries) {
      // Get account ID by account number
      let accountId: string | undefined = entry.accountId;
      
      if (!accountId) {
        // Find the account by account number
        const account = await getGLAccountByNumber(entry.accountNumber);
        if (!account) {
          throw new Error(`GL Account with number ${entry.accountNumber} not found`);
        }
        accountId = account.id;
      }
      
      if (!accountId) {
        throw new Error(`Could not find GL Account ID for account number ${entry.accountNumber}`);
      }
      
      glEntries.push({
        accountId,
        accountNumber: entry.accountNumber,
        accountName: entry.accountName,
        debit: entry.debit,
        credit: entry.credit
      });
    }
    
    // Create the GL transaction
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate: purchaseReceipt.receiptDate,
      description: `Purchase Receipt #${purchaseReceipt.receiptNumber} - ${purchaseReceipt.vendorName}`,
      referenceType: 'purchase_receipt',
      referenceId: purchaseReceipt.id as string,
      entries: glEntries
    };
    
    // Create the transaction in Firestore
    const transactionId = await createGLTransaction(glTransaction);
    
    // Update the purchase receipt with the GL transaction ID
    if (purchaseReceipt.id) {
      await updatePurchaseReceiptWithGLTransaction(purchaseReceipt.id, transactionId);
    }
    
    return transactionId;
  } catch (error) {
    console.error('Error creating purchase receipt GL transaction:', error);
    throw error;
  }
};
